import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { B2BUnitHierarchyRepository } from '@asm/features/sync/repositories/b2bunit-hierarchy.repository';
import { B2BUnitDetails } from '@shared/models/b2b-unit-details.model';
import { ASM_UNIT_ID_KEY } from '@shared/tools/constants';
import { MoneyValue } from '@asm/features/quick-order/models/cart.model';

/**
 * Serviço utilitário compartilhado entre os handlers do interceptor offline.
 * Centraliza helpers de formatação, parsing e acesso a dados de contexto.
 */
@Injectable({ providedIn: 'root' })
export class OfflineHelpersService {
  private readonly b2bUnitDetailsRepo = inject(B2BUnitHierarchyRepository);

  /**
   * Retorna os detalhes da unidade B2B sincronizada.
   *
   */
  async readStoredB2BUnitDetails(): Promise<B2BUnitDetails | null> {
    const uid = String(localStorage.getItem(ASM_UNIT_ID_KEY) ?? '').trim();

    if (uid) {
      const byKey = await firstValueFrom(this.b2bUnitDetailsRepo.getByKey(uid));
      if (byKey) return byKey;
    }

    return null;
  }

  moneyValue(value: number): MoneyValue {
    return {
      value,
      formattedValue: this.formatCurrency(value),
    };
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  parseNumericValue(value: string | number | null | undefined): number {
    if (typeof value === 'number') {
      return Number.isNaN(value) ? 0 : value;
    }

    const normalized = String(value ?? '')
      .trim()
      .replace(',', '.');
    if (!normalized) {
      return 0;
    }

    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  findDescription(items: Array<{ code?: string; description?: string }>, code?: string): string | undefined {
    if (!code) {
      return undefined;
    }

    return items.find(item => item.code === code)?.description;
  }

  parseDateOnly(value: string | null | undefined): Date | null {
    const datePart = String(value ?? '')
      .trim()
      .split('T')[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return null;
    }

    const [year, month, day] = datePart.split('-').map(part => Number(part));
    if ([year, month, day].some(part => Number.isNaN(part))) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  isSameDate(left: Date, right: Date): boolean {
    return (
      left.getFullYear() === right.getFullYear() &&
      left.getMonth() === right.getMonth() &&
      left.getDate() === right.getDate()
    );
  }
}
